import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import CastingCard from '@/components/CastingCard';
import { CASTING } from '@/constants/testIds';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Loader2, Briefcase, MapPin, Clock, IndianRupee, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

export const Castings = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get('/castings').then(({ data }) => setItems(data.castings)).finally(() => setLoading(false));
  }, []);
  return (
    <div className="pt-28 pb-24 max-w-7xl mx-auto px-6 sm:px-12">
      <div className="text-center mb-12">
        <div className="text-xs uppercase tracking-[0.3em] text-emerald-300 mb-3">Auditions</div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold text-white tracking-tight mb-4">Open <span className="rainbow-text">castings</span></h1>
        <p className="text-slate-400 max-w-xl mx-auto">From streaming pilots to brand anthems — apply with one tap.</p>
      </div>
      {loading ? <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-fuchsia-400" /></div> :
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">{items.map((c) => <CastingCard key={c.casting_id} casting={c} />)}</div>
      }
    </div>
  );
};

export const CastingDetail = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const [c, setC] = useState(null);
  const [message, setMessage] = useState('');
  const [video, setVideo] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { api.get(`/castings/${id}`).then(({ data }) => setC(data.casting)); }, [id]);
  if (!c) return <div className="pt-32 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-fuchsia-400" /></div>;

  const apply = async (e) => {
    e.preventDefault();
    if (!user) { toast.error('Please sign in to apply'); return; }
    setSubmitting(true);
    try {
      await api.post(`/castings/${id}/apply`, { message, video_url: video });
      toast.success('Application submitted!', { description: 'You will be notified if shortlisted.' });
      setMessage(''); setVideo('');
    } catch (err) {
      toast.error('Could not submit', { description: err?.response?.data?.detail });
    } finally { setSubmitting(false); }
  };

  return (
    <div className="pt-28 pb-24 max-w-4xl mx-auto px-6 sm:px-12">
      <div className="glass-strong rounded-3xl p-10 border border-white/10">
        <div className="text-xs uppercase tracking-widest text-emerald-300 mb-3 flex items-center gap-2">
          <Briefcase className="h-3.5 w-3.5" /> {c.role_type}
        </div>
        <h1 className="font-display text-4xl font-bold text-white tracking-tight mb-2">{c.title}</h1>
        <div className="text-sm text-slate-400 mb-6">{c.company}</div>
        <p className="text-slate-300 leading-relaxed mb-6">{c.description}</p>
        {c.requirements && (
          <div className="rounded-xl bg-white/5 border border-white/10 p-5 text-sm text-slate-300 mb-6">
            <div className="text-[10px] uppercase tracking-widest text-cyan-300 mb-1">Requirements</div>
            {c.requirements}
          </div>
        )}
        <div className="grid grid-cols-3 gap-4 text-sm text-slate-300 mb-8 py-4 border-y border-white/5">
          <div><MapPin className="h-3.5 w-3.5 inline mr-1 text-cyan-300" />{c.location}</div>
          <div><Clock className="h-3.5 w-3.5 inline mr-1 text-fuchsia-300" />Apply by {c.deadline}</div>
          <div><IndianRupee className="h-3.5 w-3.5 inline mr-1 text-amber-300" />{c.budget || '—'}</div>
        </div>

        <form onSubmit={apply} className="space-y-4">
          <div>
            <Label className="text-xs text-slate-400 uppercase tracking-wider">Your pitch</Label>
            <Textarea required value={message} onChange={(e) => setMessage(e.target.value)} rows={5} placeholder="Why are you the perfect fit?" data-testid={CASTING.applyMessage} className="bg-white/5 border-white/10 mt-1" />
          </div>
          <div>
            <Label className="text-xs text-slate-400 uppercase tracking-wider">Audition video URL (optional)</Label>
            <Input value={video} onChange={(e) => setVideo(e.target.value)} placeholder="YouTube / Vimeo / Drive link" data-testid={CASTING.applyVideo} className="bg-white/5 border-white/10 mt-1" />
          </div>
          <Button type="submit" disabled={submitting} data-testid={CASTING.applySubmit} className="btn-rainbow rounded-full px-7 py-6 text-white border-0">
            {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Submitting…</> : <><Sparkles className="h-4 w-4 mr-2" />Apply now</>}
          </Button>
        </form>
      </div>
    </div>
  );
};
